import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { PINCODE_MUST_BE_CHANGED_MODAL_ID } from "../../../core/modal/modal-id";
import { ChangePincodeState, useChangePincodeMachine } from "../../../machine/change-pincode-machine";
import { UnstyledButton } from "../../common/buttons/default-button";
import { PincodeKeyboard } from "../../common/keyboard/pincode-keyboard";
import { Modal } from "../../common/modal/modal";
import { WhiteModalContainer } from "../../common/modal/white-modal-container";
import { StaticAssets } from "../../styles/custom-assets";
import { theme } from "../../styles/theme";
import { PincodeChangeExplanation } from "./pincode-change-explanation";
import { PincodeChangeSuccess } from "./pincode-change-success";

interface PincodeChangeModalProps {
  type: "skip-explanation" | "operation-requirement" | "after-authentication";
}
export const PincodeChangeModal = (props: PincodeChangeModalProps) => {
  const { type } = props;
  const { state, context, submitCurrentPincode, submitNewPincode, confirmNewPincode, startPincodeChange, goBack } =
    useChangePincodeMachine(type === "skip-explanation");
  const { errorMessage } = context;
  const { formatMessage } = useIntl();
  const dismissModal = () => Modal.dismiss(PINCODE_MUST_BE_CHANGED_MODAL_ID);
  const renderPincodeMachineState = () => {
    const keyboard = context.keyboard;
    switch (state) {
      case ChangePincodeState.Explaining:
        return <PincodeChangeExplanation startPincodeChange={startPincodeChange} abort={dismissModal} type={type} />;
      case ChangePincodeState.FetchingCurrentPincodeKeyboard:
      case ChangePincodeState.FetchingCurrentPincodeKeyboardAfterError:
      case ChangePincodeState.PromptingCurrentPincodeKeyboard:
        return (
          <>
            <Illustration src={StaticAssets.settingsPincode} alt="" />
            <PincodeKeyboard
              title={formatMessage("pincodeChangeFlow.currentCodeLabel")}
              errorMessage={errorMessage}
              keyboard={keyboard}
              loadingKeyboard={
                state === ChangePincodeState.FetchingCurrentPincodeKeyboard ||
                state === ChangePincodeState.FetchingCurrentPincodeKeyboardAfterError
              }
              onSubmit={submitCurrentPincode}
            />
          </>
        );
      case ChangePincodeState.FetchingChangePincodeKeyboard:
      case ChangePincodeState.PromptingChangePincodeKeyboard:
      case ChangePincodeState.PincodePromptNotMatch:
        return (
          <>
            <Illustration src={StaticAssets.settingsPincode} alt="" />
            <PincodeKeyboard
              title={formatMessage("pincodeChangeFlow.newCodeLabel")}
              errorMessage={
                state === ChangePincodeState.PincodePromptNotMatch
                  ? formatMessage("pincodeChangeFlow.newCodesDontMatchError")
                  : errorMessage
              }
              keyboard={keyboard}
              loadingKeyboard={state === ChangePincodeState.FetchingChangePincodeKeyboard}
              onSubmit={submitNewPincode}
            />
            <BottomLink type="button" onClick={goBack}>
              {formatMessage("common.goBack")}
            </BottomLink>
          </>
        );
      case ChangePincodeState.PromptingConfirmPincodeKeyboard:
      case ChangePincodeState.RequestingPincodeChange:
        return (
          <>
            <Illustration src={StaticAssets.settingsPincode} alt="" />
            <PincodeKeyboard
              title={formatMessage("pincodeChangeFlow.newCodeConfirmationLabel")}
              errorMessage={errorMessage}
              keyboard={keyboard}
              loadingKeyboard={state === ChangePincodeState.RequestingPincodeChange}
              onSubmit={confirmNewPincode}
            />
            <BottomLink type="button" onClick={goBack}>
              {formatMessage("common.goBack")}
            </BottomLink>
          </>
        );
      case ChangePincodeState.Done:
        return <PincodeChangeSuccess onComplete={dismissModal} />;
    }
  };
  return (
    <ModalContainer closeButton id={PINCODE_MUST_BE_CHANGED_MODAL_ID}>
      {renderPincodeMachineState()}
    </ModalContainer>
  );
};

const ModalContainer = styled(WhiteModalContainer)`
  padding: 56px 28px 28px;
`;

/* The drawing announcing the step, as the mockup opens on. */
const Illustration = styled.img`
  display: block;
  width: 72px;
  height: 72px;
  margin: 0 auto 24px;
`;

/* The single way out at the foot of the keypad: back a step, or out of the flow altogether. */
const BottomLink = styled(UnstyledButton)`
  ${theme.text};
  display: block;
  margin: 28px auto 0;
  font-size: 15px;
  color: ${theme.colors.blue[700]};
`;
